import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useApi } from '../hooks/useApi';
import { usePermissions } from '../hooks/usePermissions';
import ToolbarButton from '../components/ToolbarButton';
import { WaveLoading } from '../components/Loader';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  Fab,
  Tooltip,
  TablePagination,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Add as AddIcon,
  PlayArrow as StartIcon,
  Stop as StopIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Coffee as CoffeeIcon,
  Restaurant as LunchIcon,
  Close as CloseIcon,
  Tune as TuneIcon,
} from '@mui/icons-material';

const breakTypes = [
  { value: 'coffee', label: 'Coffee Break', icon: <CoffeeIcon />, color: '#8B4513' },
  { value: 'lunch', label: 'Lunch Break', icon: <LunchIcon />, color: '#FF6B35' },
  { value: 'other', label: 'Other', icon: <AddIcon />, color: '#607D8B' },
];

const BreakAndLeisure = () => {
  const { user } = useAuth();
  const { apiCall } = useApi();
  const theme = useTheme();
  const { canPerformActionByRoute, moduleIdMap, loading: permissionsLoading } = usePermissions();
  const breakLeisureModuleId = moduleIdMap['Break & Leisure'];
  
  // Permission checks
  const canAdd = user?.role === 'admin' || canPerformActionByRoute('/break-leisure', 'Add');
  const canEdit = user?.role === 'admin' || canPerformActionByRoute('/break-leisure', 'Edit');
  const canDelete = user?.role === 'admin' || canPerformActionByRoute('/break-leisure', 'Delete');
  const canView = user?.role === 'admin' || canPerformActionByRoute('/break-leisure', 'View');
  
  // Field visibility state
  const [hiddenFields, setHiddenFields] = useState([]);
  
  // Fetch field visibility for Break & Leisure module
  useEffect(() => {
    const fetchFieldVisibility = async () => {
      if (!user?.serviceid || !breakLeisureModuleId) {
        setHiddenFields([]);
        return;
      }
      
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/service-management/field-visibility/${user.serviceid}/${breakLeisureModuleId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        
        if (response.ok) {
          const data = await response.json();
          setHiddenFields(data.hiddenFields || []);
        } else {
          setHiddenFields([]);
        }
      } catch (error) {
        console.error('Error fetching field visibility:', error);
        setHiddenFields([]);
      }
    };
    
    fetchFieldVisibility();
  }, [user?.serviceid, breakLeisureModuleId]);
  
  // Helper to check if a field is hidden
  const isFieldHidden = (fieldName) => {
    return hiddenFields.includes(fieldName);
  };
  
  const [breaks, setBreaks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 10, totalPages: 0 });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBreak, setEditingBreak] = useState(null);
  const [activeBreak, setActiveBreak] = useState(null);
  const [formData, setFormData] = useState({
    userId: '',
    type: '',
    description: '',
    startTime: '',
    endTime: '',
    duration: '',
  });
  const [selectedDate, setSelectedDate] = useState(() => {
    const t = new Date();
    return `${t.getFullYear()}-${String(t.getMonth()+1).padStart(2,'0')}-${String(t.getDate()).padStart(2,'0')}`;
  });
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [employeeFilter, setEmployeeFilter] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [breakToDelete, setBreakToDelete] = useState(null);
  const [savingBreak, setSavingBreak] = useState(false);
  const [deletingBreak, setDeletingBreak] = useState(false);
  const [endingBreak, setEndingBreak] = useState(false);
  const endingBreakRef = useRef(false);
  const [startingBreak, setStartingBreak] = useState(false);
  const startingBreakRef = useRef(false);
  const [submittingBreak, setSubmittingBreak] = useState(false);
  const submittingBreakRef = useRef(false);
  
  // Timer state (for dialog-based timer only)
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [timerStartTime, setTimerStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  
  // Refs to avoid stale closures in event handlers (for tab/browser switching)
  const activeBreakRef = useRef(null);
  const timerStartTimeRef = useRef(null);
  
  // Update refs when state changes
  useEffect(() => {
    activeBreakRef.current = activeBreak;
  }, [activeBreak]);
  
  useEffect(() => {
    timerStartTimeRef.current = timerStartTime;
  }, [timerStartTime]);

  // ✅ NEW: function to check for active break
  const checkForActiveBreak = async (retry = 0) => {
    try {
      const response = await apiCall(`${process.env.REACT_APP_API_URL}/breaks/active`);
      if (!response.ok) throw new Error('Failed to fetch active break');
      const data = await response.json();
      if (data) {
        setActiveBreak(data);
        const start = new Date(data.startTime);
        setTimerStartTime(start);
        const elapsedSec = Math.floor((Date.now() - start.getTime()) / 1000);
        setElapsedTime(elapsedSec);
        setIsTimerRunning(true);
      } else {
        setActiveBreak(null);
        setTimerStartTime(null);
        setIsTimerRunning(false);
        setElapsedTime(0);
      }
    } catch (err) {
      console.error('checkForActiveBreak error:', err);
      if (retry < 3) setTimeout(() => checkForActiveBreak(retry + 1), (2 ** retry) * 1000);
    }
  };

  // Update existing useEffect (user dependency) to call checkForActiveBreak
  useEffect(() => {
    if (user) {
      fetchBreaks(1, 10000);
      if (['admin', 'project_manager', 'team_leader', 'view_only', 'cmo'].includes(user?.role)) {
        fetchUsers();
      }
      resetForm();
      checkForActiveBreak(); // ✅ call here
    }
  }, [user]);

  // Helper function to calculate elapsed time
  const calculateElapsedTime = () => {
    if (activeBreak && activeBreak.startTime) {
      // Use database time for persisted breaks (always accurate)
      const start = new Date(activeBreak.startTime);
      const elapsedSec = Math.floor((Date.now() - start.getTime()) / 1000);
      return elapsedSec;
    } else if (timerStartTime) {
      // Use local time for dialog timer (before break is saved)
      const start = new Date(timerStartTime);
      const elapsedSec = Math.floor((Date.now() - start.getTime()) / 1000);
      return elapsedSec;
    }
    return 0;
  };

  // Update timer effect - always calculate from database when activeBreak exists
  useEffect(() => {
    if (!isTimerRunning) {
      return;
    }

    // Calculate immediately (important for tab switching)
    const updateElapsed = () => {
      // Always prefer database time if activeBreak exists (most accurate)
      if (activeBreakRef.current && activeBreakRef.current.startTime) {
        const start = new Date(activeBreakRef.current.startTime);
        const elapsedSec = Math.floor((Date.now() - start.getTime()) / 1000);
        setElapsedTime(elapsedSec);
      } else if (timerStartTimeRef.current) {
        // Fallback to local timer
        const start = new Date(timerStartTimeRef.current);
        const elapsedSec = Math.floor((Date.now() - start.getTime()) / 1000);
        setElapsedTime(elapsedSec);
      }
    };

    // Update immediately
    updateElapsed();

    // Set interval for continuous updates (may be throttled in background tabs, but that's OK)
    const interval = setInterval(updateElapsed, 1000);

    return () => clearInterval(interval);
  }, [isTimerRunning, activeBreak, timerStartTime]);

  // Periodic sync every 10s when active break exists (more frequent for accuracy)
  useEffect(() => {
    if (activeBreak) {
      // Sync immediately
      checkForActiveBreak();
      
      // Then sync every 10 seconds
      const id = setInterval(() => {
        checkForActiveBreak();
      }, 10000);
      
      return () => clearInterval(id);
    }
  }, [activeBreak]);

  // Visibility change handler - sync from database when tab becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && user) {
        // Always sync from database when tab becomes visible (handles tab/browser switching)
        // Use refs to avoid stale closures
        if (activeBreakRef.current) {
          // Force sync from database to get accurate time
          checkForActiveBreak();
        } else if (timerStartTimeRef.current) {
          // Recalculate from local timer if no active break
          const start = new Date(timerStartTimeRef.current);
          const elapsedSec = Math.floor((Date.now() - start.getTime()) / 1000);
          setElapsedTime(elapsedSec);
        }
      }
    };

    // Also handle window focus (browser switching)
    const handleWindowFocus = () => {
      if (user) {
        // Use refs to avoid stale closures
        if (activeBreakRef.current) {
          checkForActiveBreak();
        } else if (timerStartTimeRef.current) {
          const start = new Date(timerStartTimeRef.current);
          const elapsedSec = Math.floor((Date.now() - start.getTime()) / 1000);
          setElapsedTime(elapsedSec);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleWindowFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, [user]);

  // Browser close/tab close warning when timer is running
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (isTimerRunning) {
        event.preventDefault();
        event.returnValue = 'Timer is running! Your break will not be logged if you leave this page.';
        return 'Timer is running! Your break will not be logged if you leave this page.';
      }
    };

    if (isTimerRunning) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isTimerRunning]);


  const fetchBreaks = async (page = 1, limit = 10000) => {
    setLoading(true);
    try {
      // Team members should use the regular breaks endpoint (their own breaks only)
      // Admins, project managers, team leaders, and view_only users can use the /all endpoint
      const url = ['admin', 'project_manager', 'team_leader', 'view_only', 'cmo'].includes(user?.role)
        ? `${process.env.REACT_APP_API_URL}/breaks/all?page=${page}&limit=${limit}`
        : `${process.env.REACT_APP_API_URL}/breaks?page=${page}&limit=${limit}`;
      
      console.log('Fetching breaks from URL:', url);
      console.log('User role:', user?.role);
      
      const response = await apiCall(url);
      
      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(`Failed to fetch breaks: ${response.status} ${errorText}`);
      }
      
      const data = await response.json();
      console.log('Fetched breaks:', data);
      setBreaks(Array.isArray(data) ? data : data.data || []);
      if (data.pagination) {
        setPagination(data.pagination);
      }
    } catch (err) {
      console.error('Fetch breaks error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Filter breaks by selected date and employee
  const getFilteredBreaks = () => {
    let filtered = breaks;
    
    // Filter by date if selected
    if (selectedDate) {
      const selectedDateObj = new Date(selectedDate);
      const startOfDay = new Date(selectedDateObj.getFullYear(), selectedDateObj.getMonth(), selectedDateObj.getDate());
      const endOfDay = new Date(selectedDateObj.getFullYear(), selectedDateObj.getMonth(), selectedDateObj.getDate(), 23, 59, 59);
      
      filtered = filtered.filter(breakItem => {
        const breakDate = new Date(breakItem.startTime);
        return breakDate >= startOfDay && breakDate <= endOfDay;
      });
    }
    
    // Filter by employee if selected
    if (employeeFilter) {
      filtered = filtered.filter(breakItem => 
        breakItem.user?.name?.toLowerCase().includes(employeeFilter.toLowerCase())
      );
    }
    
    return filtered;
  };

  const fetchUsers = async () => {
    try {
      console.log('Fetching users...');
      
      // Use project management endpoint for all roles to get all users
      const response = await apiCall(`${process.env.REACT_APP_API_URL}/users/for-project-management`);
      console.log('Users response status:', response.status);
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Users error response:', errorText);
        throw new Error(`Failed to fetch users: ${response.status} ${errorText}`);
      }
      const data = await response.json();
      console.log('Fetched users:', data);
      setUsers(Array.isArray(data) ? data : data.data || []);
    } catch (err) {
      console.error('Error fetching users:', err);
      // Fallback to active users if project management endpoint fails
      try {
        const fallbackResponse = await apiCall(`${process.env.REACT_APP_API_URL}/users/active`);
        if (fallbackResponse.ok) {
          const fallbackData = await fallbackResponse.json();
          setUsers(Array.isArray(fallbackData) ? fallbackData : fallbackData.data || []);
        } else {
          setUsers([]);
        }
      } catch (fallbackErr) {
        console.error('Fallback user fetch failed:', fallbackErr);
        setUsers([]);
      }
    }
  };

  const handleStartBreak = async () => {
    // Simple ref check to prevent multiple clicks
    if (startingBreakRef.current) return;
    
    // Set ref and state immediately
    startingBreakRef.current = true;
    setStartingBreak(true);
    
    try {
      // Convert startTime (if present) to ISO string to ensure consistency across environments
      const startPayload = { ...formData };
      if (startPayload.startTime) {
        const s = new Date(startPayload.startTime);
        if (!isNaN(s)) {
          startPayload.startTime = s.toISOString();
        }
      }

      const response = await apiCall(`${process.env.REACT_APP_API_URL}/breaks/start`, {
        method: 'POST',
        body: JSON.stringify(startPayload),
      });
      if (!response.ok) throw new Error('Failed to start break');
      const data = await response.json();
      setActiveBreak(data);
      setDialogOpen(false);
      fetchBreaks();
      
      // Timer will automatically start because activeBreak is set and isTimerRunning will be true
      // Set timer state to match the database break
      const startTime = new Date(data.startTime);
      setTimerStartTime(startTime);
      setIsTimerRunning(true);
      const elapsedSec = Math.floor((Date.now() - startTime.getTime()) / 1000);
      setElapsedTime(elapsedSec);
    } catch (err) {
      setError(err.message);
    } finally {
      startingBreakRef.current = false;
      setStartingBreak(false);
    }
  };

  const handleEndBreak = async (e) => {
    // Simple ref check to prevent multiple clicks
    if (endingBreakRef.current) return;
    
    // Set ref and state immediately
    endingBreakRef.current = true;
    setEndingBreak(true);
    setError(''); // Clear any previous errors
    
    try {
      const response = await apiCall(`${process.env.REACT_APP_API_URL}/breaks/${activeBreak.id}/end`, {
        method: 'PUT',
      });
      
      if (!response.ok) {
        // Try to read the actual error message from the response
        let errorMessage = 'Failed to end break';
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch (parseError) {
          // If response is not JSON, use status-based message
          if (response.status === 403) {
            errorMessage = 'You do not have permission to end this break.';
          } else if (response.status === 409) {
            errorMessage = 'This break has already been ended. Refreshing...';
            // Refresh break status if break was already ended
            setTimeout(() => {
              checkForActiveBreak();
            }, 1000);
          } else if (response.status === 500) {
            errorMessage = 'Server error. Please try again or contact support.';
          }
        }
        throw new Error(errorMessage);
      }
      
      const data = await response.json();
      setActiveBreak(null);
      fetchBreaks();
      
      // Stop the timer when break ends
      stopTimer();
      
      // Clear error on success
      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      endingBreakRef.current = false;
      setEndingBreak(false);
    }
  };

  const handleSubmit = async () => {
    // Prevent multiple rapid clicks
    if (loading || savingBreak) return;

    try {
      console.log('Submit - Current form data:', formData);
      console.log('Submit - User role:', user?.role);
      console.log('Submit - User ID:', user?.id);
      
      // Validate required fields
      if (['admin', 'project_manager', 'team_leader', 'cmo'].includes(user?.role) && !formData.userId) {
        setError('Please select an employee');
        return;
      }
      if (!formData.type) {
        setError('Please select a break type');
        return;
      }
      if (!formData.startTime) {
        setError('Please select a start time');
        return;
      }

      const url = editingBreak 
        ? `${process.env.REACT_APP_API_URL}/breaks/${editingBreak.id}`
        : `${process.env.REACT_APP_API_URL}/breaks`;
      
      // Ensure userId is set for team members
      const requestData = { ...formData };

      // ==== NEW: normalize date fields to ISO (UTC) to avoid timezone shifts ====
      if (requestData.startTime) {
        const start = new Date(requestData.startTime);
        if (!isNaN(start)) {
          requestData.startTime = start.toISOString();
        }
      }
      if (requestData.endTime) {
        const end = new Date(requestData.endTime);
        if (!isNaN(end)) {
          requestData.endTime = end.toISOString();
        }
      }
      // ========================================================================

      if (user?.role === 'team_member') {
        requestData.userId = user.id; // Always use the current user's ID for team members
      }
      
      console.log('Submitting break data:', requestData);
      console.log('User role:', user?.role);
      console.log('URL:', url);

      setSavingBreak(true);
      const response = await apiCall(url, {
        method: editingBreak ? 'PUT' : 'POST',
        body: JSON.stringify(requestData),
      });
      
      console.log('Response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(`Failed to save break: ${response.status} ${errorText}`);
      }
      
      setDialogOpen(false);
      setEditingBreak(null);
      resetForm();
      fetchBreaks();
      setError(''); // Clear any previous errors
    } catch (err) {
      console.error('Submit error:', err);
      setError(err.message);
    } finally {
      setSavingBreak(false);
    }
  };

  const handleDeleteClick = (breakItem) => {
    setBreakToDelete(breakItem);
    setDeleteOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!breakToDelete) return;

    setDeletingBreak(true);
    try {
      const response = await apiCall(`${process.env.REACT_APP_API_URL}/breaks/${breakToDelete.id}`, {
        method: 'DELETE',
      });
      if (!response.ok) throw new Error('Failed to delete break');
      setDeleteOpen(false);
      setBreakToDelete(null);
      fetchBreaks();
    } catch (err) {
      setError(err.message);
      setDeleteOpen(false);
      setBreakToDelete(null);
    } finally {
      setDeletingBreak(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteOpen(false);
    setBreakToDelete(null);
  };

  const handleEdit = (breakItem) => {
    setEditingBreak(breakItem);
    setFormData({
      userId: breakItem.userId || user?.id || '',
      type: breakItem.type,
      description: breakItem.description,
      startTime: breakItem.startTime,
      endTime: breakItem.endTime,
      duration: breakItem.duration,
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    const newFormData = {
      userId: ['admin', 'project_manager', 'team_leader', 'cmo'].includes(user?.role) ? '' : (user?.id || ''),
      type: '',
      description: '',
      startTime: '',
      endTime: '',
      duration: '',
    };
    console.log('Resetting form - User role:', user?.role);
    console.log('Resetting form - User ID:', user?.id);
    console.log('Resetting form - New form data:', newFormData);
    setFormData(newFormData);
  };

  const openDialog = () => {
    setEditingBreak(null);
    resetForm();
    console.log('Opening dialog - User role:', user?.role);
    console.log('Opening dialog - User ID:', user?.id);
    console.log('Opening dialog - Form data after reset:', {
      userId: ['admin', 'project_manager', 'team_leader'].includes(user?.role) ? '' : user?.id || '',
      type: '',
      description: '',
      startTime: '',
      endTime: '',
      duration: '',
    });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingBreak(null);
    resetForm();
  };

  const getBreakTypeInfo = (type) => {
    return breakTypes.find(bt => bt.value === type) || breakTypes[breakTypes.length - 1];
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  // Timer helper functions
  const formatElapsedTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    } else {
      return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
  };

  const startTimer = () => {
    // Prevent multiple rapid clicks
    if (isTimerRunning) return;
    
    const now = new Date();
    setTimerStartTime(now);
    setIsTimerRunning(true);
    setElapsedTime(0);
  };

  const stopTimer = () => {
    setIsTimerRunning(false);
    setTimerStartTime(null);
    setElapsedTime(0);
  };

  // Enhanced timer functions that work with break system
  const startBreakWithTimer = async () => {
    // First start the break
    await handleStartBreak();
    // Timer will be started automatically in handleStartBreak
  };

  const stopBreakWithTimer = async () => {
    // First stop the timer
    stopTimer();
    // Then end the break
    await handleEndBreak();
  };

  // Stop timer and submit break with timer data
  const stopTimerAndSubmit = async () => {
    // Prevent multiple rapid clicks
    if (!isTimerRunning || submittingBreakRef.current) return;
    
    // Set ref and state immediately
    submittingBreakRef.current = true;
    setSubmittingBreak(true);
    
    try {
      // Calculate start and end times based on timer
      const endTime = new Date();
      const startTime = new Date(endTime.getTime() - (elapsedTime * 1000));
      
      // Calculate duration in minutes (ensure it's at least 1 minute if there's any time)
      const durationInMinutes = Math.max(1, Math.round(elapsedTime / 60));
      
      // Prepare break data with timer information - exclude hidden fields
      const breakData = {};
      
      if (!isFieldHidden('userId')) {
        // Determine which user ID to use
        let targetUserId = user?.id;
        if (['admin', 'project_manager', 'team_leader', 'cmo'].includes(user?.role) && formData.userId) {
          targetUserId = formData.userId;
        }
        breakData.userId = targetUserId;
      }
      if (!isFieldHidden('type')) {
        breakData.type = formData.type;
      }
      if (!isFieldHidden('description')) {
        breakData.description = formData.description;
      }
      if (!isFieldHidden('startTime')) {
        breakData.startTime = startTime.toISOString();
      }
      if (!isFieldHidden('endTime')) {
        breakData.endTime = endTime.toISOString();
      }
      breakData.duration = durationInMinutes;
      
      console.log('Submitting break with timer data:', breakData);
      console.log('Elapsed time in seconds:', elapsedTime);
      console.log('Duration in minutes:', durationInMinutes);
      
      const response = await apiCall(`${process.env.REACT_APP_API_URL}/breaks`, {
        method: 'POST',
        body: JSON.stringify(breakData),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to save break: ${response.status} ${errorText}`);
      }
      
      // Stop the timer and close dialog
      stopTimer();
      setDialogOpen(false);
      resetForm();
      fetchBreaks();
      setError('');
    } catch (err) {
      console.error('Error submitting break with timer:', err);
      setError(err.message);
    } finally {
      submittingBreakRef.current = false;
      setSubmittingBreak(false);
    }
  };

  const getTodayStats = () => {
    const today = new Date().toDateString();
    const todayBreaks = breaks.filter(breakItem => 
      new Date(breakItem.startTime).toDateString() === today
    );
    
    const totalMinutes = todayBreaks.reduce((sum, breakItem) => sum + (breakItem.duration || 0), 0);
    const breakCount = todayBreaks.length;
    
    return { totalMinutes, breakCount };
  };

  const getSelectedDateStats = () => {
    if (!selectedDate) return getTodayStats();
    
    const selectedDateObj = new Date(selectedDate);
    const startOfDay = new Date(selectedDateObj.getFullYear(), selectedDateObj.getMonth(), selectedDateObj.getDate());
    const endOfDay = new Date(selectedDateObj.getFullYear(), selectedDateObj.getMonth(), selectedDateObj.getDate(), 23, 59, 59);
    
    const selectedDateBreaks = breaks.filter(breakItem => {
      const breakDate = new Date(breakItem.startTime);
      return breakDate >= startOfDay && breakDate <= endOfDay;
    });
    
    const totalMinutes = selectedDateBreaks.reduce((sum, breakItem) => sum + (breakItem.duration || 0), 0);
    const breakCount = selectedDateBreaks.length;
    
    return { totalMinutes, breakCount };
  };


  const { totalMinutes, breakCount } = getSelectedDateStats();

  if (loading) {
    return (
      <Box 
        display="flex" 
        flexDirection="column" 
        alignItems="center" 
        justifyContent="center" 
        minHeight="calc(100vh - 120px)"
        gap={3}
        sx={{
          borderRadius: 2,
          border: theme.palette.mode === 'dark'
            ? '1px solid rgba(255, 255, 255, 0.1)'
            : '1px solid rgba(0, 0, 0, 0.1)',
          mx: 2,
          my: 2,
          p: 4
        }}
      >
        <WaveLoading theme={theme} size="xl" />
        <Typography 
          variant="h5" 
          sx={{ 
            color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.8)',
            fontWeight: 500
          }}
        >
          Loading breaks...
        </Typography>
        <Typography 
          variant="body1" 
          sx={{ 
            color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
            textAlign: 'center',
            maxWidth: 400
          }}
        >
          Please wait while we fetch your break data
        </Typography>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
        Break & Leisure Management
        {user?.role === 'team_member' && (
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            You can view and add breaks, but cannot edit or delete them. Contact your manager for modifications.
          </Typography>
        )}
        {!['admin', 'project_manager', 'team_leader', 'team_member', 'cmo'].includes(user?.role) && (
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            You can only view and manage your own breaks.
          </Typography>
        )}
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {/* Filter Section — Notion-style soft bar, no "Filters" title */}
      <Box
        sx={{
          p: 2,
          mb: 3,
          bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#fafbfc',
          border: (t) => t.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e5e7eb',
          borderRadius: 2.5,
        }}
      >
        <Box display="flex" flexWrap="wrap" gap={1.5} alignItems="center">
          {/* Date — primary filter, always visible */}
          <TextField
            label="Date"
            type="date"
            size="small"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 170 }}
          />
          <Box sx={{ flexGrow: 1, minWidth: 0 }} />
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0 }}>
            <ToolbarButton
              variant={showAdvancedFilters ? 'contained' : 'outlined'}
              startIcon={<TuneIcon />}
              onClick={() => setShowAdvancedFilters((v) => !v)}
            >
              More filters
              {employeeFilter && ' •'}
            </ToolbarButton>
            {(selectedDate || employeeFilter) && (
              <ToolbarButton
                variant="outlined"
                disabled={loading}
                onClick={() => {
                  setSelectedDate('');
                  setEmployeeFilter('');
                }}
              >
                Clear
              </ToolbarButton>
            )}
          </Box>
        </Box>

        {(showAdvancedFilters || employeeFilter) && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1.5,
              alignItems: 'center',
              mt: 1.5,
              pt: 1.5,
              borderTop: (t) => t.palette.mode === 'dark' ? '1px dashed rgba(255,255,255,0.08)' : '1px dashed #e5e7eb',
            }}
          >
            <Button
              variant={(() => { const t = new Date(); return selectedDate === `${t.getFullYear()}-${String(t.getMonth()+1).padStart(2,'0')}-${String(t.getDate()).padStart(2,'0')}`; })() ? "contained" : "outlined"}
              size="small"
              onClick={() => { const t = new Date(); setSelectedDate(`${t.getFullYear()}-${String(t.getMonth()+1).padStart(2,'0')}-${String(t.getDate()).padStart(2,'0')}`); }}
            >
              Today
            </Button>
            <Button
              variant={(() => { const t = new Date(); t.setDate(t.getDate()-1); return selectedDate === `${t.getFullYear()}-${String(t.getMonth()+1).padStart(2,'0')}-${String(t.getDate()).padStart(2,'0')}`; })() ? "contained" : "outlined"}
              size="small"
              onClick={() => { const t = new Date(); t.setDate(t.getDate()-1); setSelectedDate(`${t.getFullYear()}-${String(t.getMonth()+1).padStart(2,'0')}-${String(t.getDate()).padStart(2,'0')}`); }}
            >
              Yesterday
            </Button>
            <Button
              variant={selectedDate === '' ? "contained" : "outlined"}
              size="small"
              onClick={() => setSelectedDate('')}
            >
              All Time
            </Button>
            {/* Employee Filter - Only for PM, TL, Admin, View Only, CMO */}
            {(user?.role === 'admin' || user?.role === 'project_manager' || user?.role === 'team_leader' || user?.role === 'view_only' || user?.role === 'cmo') && (
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Filter by Employee</InputLabel>
                <Select
                  value={employeeFilter}
                  onChange={(e) => setEmployeeFilter(e.target.value)}
                  label="Filter by Employee"
                >
                  <MenuItem value="">All Employees</MenuItem>
                  {users.map((user) => (
                    <MenuItem key={user.id} value={user.name}>
                      {user.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        )}
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={3} sx={{ textAlign: 'center', p: 3 }}>
            <Typography variant="h6" color="primary" sx={{ fontWeight: 700 }}>
              {breakCount}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {selectedDate ? 'Breaks on Selected Date' : 'Breaks Today'}
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={3} sx={{ textAlign: 'center', p: 3 }}>
            <Typography variant="h6" color="primary" sx={{ fontWeight: 700 }}>
              {formatDuration(totalMinutes)}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {selectedDate ? 'Break Time on Selected Date' : 'Total Break Time'}
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={3} sx={{ textAlign: 'center', p: 3 }}>
            <Typography variant="h6" color="primary" sx={{ fontWeight: 700 }}>
              {breakCount > 0 ? formatDuration(Math.round(totalMinutes / breakCount)) : '0m'}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Average Break Duration
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={3} sx={{ textAlign: 'center', p: 3 }}>
            <Typography variant="h6" color="primary" sx={{ fontWeight: 700 }}>
              {activeBreak ? 'Active' : 'None'}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Current Break Status
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Active Break Card */}
      {activeBreak && (
        <Card sx={{ mb: 3, bgcolor: 'success.light', color: 'white' }}>
          <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Active Break: {getBreakTypeInfo(activeBreak.type).label}
              </Typography>
              <Typography variant="body2">
                Started at {new Date(activeBreak.startTime).toLocaleTimeString()}
              </Typography>
            </Box>
            {/* Show End Break button only if user has Edit permission OR owns this break */}
            {(canEdit || activeBreak.userId === user?.id) && (
              <Button
                variant="contained"
                color="error"
                startIcon={endingBreak ? <CircularProgress size={20} color="inherit" /> : <StopIcon />}
                onClick={handleEndBreak}
                disabled={endingBreak}
              >
                {endingBreak ? 'Ending...' : 'End Break'}
              </Button>
            )}
            {/* Show message if user can't end this break */}
            {!canEdit && activeBreak.userId !== user?.id && (
              <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                Contact your manager to end this break
              </Typography>
            )}
          </CardContent>
        </Card>
      )}
      
      {/* Break History Table */}
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Break History
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, mb: 3, alignItems: 'center', flexWrap: 'wrap' }}>
            {canAdd && (
              <ToolbarButton
                variant="contained"
                startIcon={<AddIcon />}
                onClick={openDialog}
                disabled={!!activeBreak}
              >
                {['admin', 'project_manager', 'team_leader', 'cmo'].includes(user?.role) ? 'Add Break for Employee' : 'Add Break'}
              </ToolbarButton>
            )}
            <Box sx={{ flexGrow: 1, minWidth: 0 }} />
          </Box>
          
          {permissionsLoading ? (
            <Box
              sx={{
                textAlign: 'center',
                py: 8,
                minHeight: '400px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 2,
                border: theme.palette.mode === 'dark'
                  ? '1px solid rgba(255, 255, 255, 0.1)'
                  : '1px solid rgba(0, 0, 0, 0.1)',
              }}
            >
              <WaveLoading theme={theme} size="large" />
            </Box>
          ) : !canView ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="textSecondary">
                You don't have permission to view this data.
              </Typography>
            </Box>
          ) : (
          <TableContainer
            component={Paper}
            sx={{
              maxHeight: 400,
              border: theme.palette.mode === 'dark'
                ? '1px solid rgba(255, 255, 255, 0.1)'
                : '1px solid rgba(0, 0, 0, 0.1)',
              borderRadius: 2,
              overflowX: 'auto',
              overflowY: 'auto',
              '&::-webkit-scrollbar': {
                height: 8,
                width: 8,
              },
              '&::-webkit-scrollbar-track': {
                background: theme.palette.mode === 'dark' 
                  ? 'rgba(255, 255, 255, 0.1)' 
                  : 'rgba(0, 0, 0, 0.1)',
                borderRadius: 4,
              },
              '&::-webkit-scrollbar-thumb': {
                background: theme.palette.mode === 'dark' 
                  ? 'rgba(255, 255, 255, 0.3)' 
                  : 'rgba(0, 0, 0, 0.3)',
                borderRadius: 4,
                '&:hover': {
                  background: theme.palette.mode === 'dark' 
                    ? 'rgba(255, 255, 255, 0.5)' 
                    : 'rgba(0, 0, 0, 0.5)',
                },
              },
            }}
          >
            <Table stickyHeader sx={{ minWidth: 800 }}>
              <TableHead sx={{ '& th': { backgroundColor: '#1976d2', color: '#fff', fontWeight: 700 } }}>
                <TableRow>
                  <TableCell>Employee</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Start Time</TableCell>
                  <TableCell>End Time</TableCell>
                  <TableCell>Duration</TableCell>
                  {(canEdit || canDelete) && <TableCell>Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell 

colSpan={(canEdit || canDelete) ? 7 : 6}
                      sx={{
                        textAlign: 'center',
                        py: 8,
                        border: 'none'
                      }}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <WaveLoading theme={theme} size="large" />
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.8)',
                            fontWeight: 500
                          }}
                        >
                          Loading breaks...
                        </Typography>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
                            maxWidth: 300,
                            textAlign: 'center'
                          }}
                        >
                          Please wait while we fetch the latest break data
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : getFilteredBreaks().length > 0 ? (
                  getFilteredBreaks().slice((pagination.page - 1) * pagination.limit, (pagination.page - 1) * pagination.limit + pagination.limit).map((breakItem) => {
                    const breakType = getBreakTypeInfo(breakItem.type);
                    return (
                      <TableRow
                        key={breakItem.id}
                        hover
                        sx={{
                          borderBottom: theme.palette.mode === 'dark'
                            ? '1px solid rgba(255, 255, 255, 0.05)'
                            : '1px solid rgba(0, 0, 0, 0.05)',
                          '&:hover': {
                            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.03)',
                          }
                        }}
                      >
                        <TableCell sx={{ 
                          fontWeight: 500,
                          color: theme.palette.mode === 'dark' ? 'white' : '#2C3034'
                        }}>
                          {breakItem.user?.name || 'Unknown'}
                        </TableCell>
                        <TableCell sx={{ 
                          color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : '#2C3034',
                          fontWeight: 400
                        }}>
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.5,
                              px: 1.5,
                              py: 0.5,
                              borderRadius: 1,
                              bgcolor: breakType.color,
                              color: 'white',
                              fontSize: '0.75rem',
                              fontWeight: 500,
                              backdropFilter: 'blur(10px)',
                              boxShadow: theme.palette.mode === 'dark' 
                                ? '0 2px 8px rgba(0, 0, 0, 0.2)' 
                                : '0 2px 8px rgba(0, 0, 0, 0.1)'
                            }}
                          >
                            {breakType.icon}
                            {breakType.label}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ 
                          color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : '#2C3034',
                          fontWeight: 400
                        }}>{breakItem.description || '-'}</TableCell>
                        <TableCell sx={{ 
                          color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : '#2C3034',
                          fontWeight: 400
                        }}>
                          {new Date(breakItem.startTime).toLocaleString()}
                        </TableCell>
                        <TableCell sx={{ 
                          color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : '#2C3034',
                          fontWeight: 400
                        }}>
                          {breakItem.endTime 
                            ? new Date(breakItem.endTime).toLocaleString()
                            : 'Active'
                          }
                        </TableCell>
                        <TableCell sx={{ 
                          color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : '#2C3034',
                          fontWeight: 400
                        }}>
                          {breakItem.duration ? formatDuration(breakItem.duration) : '-'}
                        </TableCell>
                        {(canEdit || canDelete) && (
                          <TableCell sx={{ 
                            color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : '#2C3034',
                            fontWeight: 400
                          }}>
                            {canEdit && (
                              <IconButton
                                size="small"
                                onClick={() => handleEdit(breakItem)}
                                disabled={!breakItem.endTime}
                                sx={{
                                  color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : '#2C3034',
                                  '&:hover': {
                                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.03)',
                                  }
                                }}
                              >
                                <EditIcon />
                              </IconButton>
                            )}
                            {canDelete && (
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteClick(breakItem)}
                                sx={{
                                  color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : '#2C3034',
                                  '&:hover': {
                                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.03)',
                                  }
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={(canEdit || canDelete) ? 7 : 6} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="textSecondary">
                        {selectedDate 
                          ? `No breaks found for ${new Date(selectedDate).toLocaleDateString()}`
                          : 'No breaks found'
                        }
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          )}
          
          <TablePagination
            component="div"
            count={Math.max(0, getFilteredBreaks().length)}
            page={Math.max(0, Math.min(pagination.page - 1, Math.max(0, Math.ceil(getFilteredBreaks().length / pagination.limit) - 1)))}
            onPageChange={(event, newPage) => setPagination(prev => ({ ...prev, page: newPage + 1 }))}
            rowsPerPage={Math.max(1, Math.min(100, pagination.limit))}
            onRowsPerPageChange={(event) => {
              const newLimit = parseInt(event.target.value, 10);
              if (isNaN(newLimit) || newLimit <= 0) return;
              setPagination(prev => ({ ...prev, limit: newLimit, page: 1 }));
            }}
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            showFirstButton
            showLastButton
          />
        </CardContent>
      </Card>

      {/* Add/Edit Break Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={(event, reason) => {
          // Only close on explicit actions (Cancel button, etc.), not on backdrop click or Escape key
          if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
            closeDialog();
          }
        }}
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" component="div">
            {editingBreak 
              ? (['admin', 'project_manager', 'team_leader', 'cmo'].includes(user?.role) ? 'Edit Break' : (user?.role === 'team_member' ? 'View Break (Read Only)' : 'Edit Your Break'))
              : (['admin', 'project_manager', 'team_leader', 'cmo'].includes(user?.role) ? 'Add Break for Employee' : 'Add New Break')
            }
          </Typography>
          <IconButton
            onClick={closeDialog}
            disabled={isTimerRunning}
            sx={{ 
              color: isTimerRunning ? 'text.disabled' : 'text.primary',
              '&:hover': {
                backgroundColor: isTimerRunning ? 'transparent' : 'action.hover'
              }
            }}
            title={isTimerRunning ? 'Cannot close while timer is running' : 'Close'}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {/* Employee Selection - Only show for admins, project managers, and team leaders */}
            {!isFieldHidden('userId') && ['admin', 'project_manager', 'team_leader', 'cmo'].includes(user?.role) && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Employee</InputLabel>
                <Select
                  value={formData.userId}
                  onChange={(e) => setFormData({ ...formData, userId: e.target.value })}
                  label="Employee"
                >
                  {users.map((userItem) => (
                    <MenuItem key={userItem.id} value={userItem.id}>
                      {userItem.name} ({userItem.email})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            
            {/* Show employee info for team members */}
            {!isFieldHidden('userId') && user?.role === 'team_member' && !editingBreak && (
              <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="body2" color="textSecondary">
                  Creating break for: <strong>{user.name}</strong>
                </Typography>
              </Box>
            )}
            
            {!isFieldHidden('type') && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Break Type</InputLabel>
                <Select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  label="Break Type"
                  disabled={user?.role === 'team_member' && editingBreak}
                >
                  {breakTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {type.icon}
                        {type.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            
            {!isFieldHidden('description') && (
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={3}
                sx={{ mb: 2 }}
                disabled={user?.role === 'team_member' && editingBreak}
              />
            )}
            
            {/* Timer-based break logging for new breaks */}
            {!editingBreak && (
              <Box sx={{ mb: 2, p: 2, bgcolor: 'primary.light', borderRadius: 1, color: 'white' }}>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                  Use Timer to Log Break Time
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Click "Start Break" to begin timing your break. The timer will automatically track the duration.
                </Typography>
                
                <Box display="flex" alignItems="center" justifyContent="center" mb={2}>
                  <Typography 
                    variant="h4" 
                    sx={{ 
                      fontWeight: 700, 
                      color: isTimerRunning ? 'white' : 'rgba(255,255,255,0.7)',
                      fontFamily: 'monospace',
                      textAlign: 'center',
                      minWidth: '150px'
                    }}
                  >
                    {formatElapsedTime(elapsedTime)}
                  </Typography>
                </Box>
                
                <Box display="flex" justifyContent="center" gap={2}>
                  {!isTimerRunning ? (
                    <Button
                      variant="contained"
                      color="secondary"
                      startIcon={<StartIcon />}
                      onClick={startTimer}
                      disabled={!formData.type || (['admin', 'project_manager', 'team_leader', 'cmo'].includes(user?.role) && !formData.userId)}
                      sx={{ minWidth: 120 }}
                    >
                      Start Break
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      color="error"
                      startIcon={submittingBreak ? <CircularProgress size={20} color="inherit" /> : <StopIcon />}
                      onClick={stopTimerAndSubmit}
                      disabled={submittingBreak}
                      sx={{ minWidth: 120 }}
                    >
                      {submittingBreak ? 'Submitting...' : 'End & Log Break'}
                    </Button>
                  )}
                </Box>
              </Box>
            )}
            
            {/* Manual time entry for editing existing breaks */}
            {editingBreak && (
              <Grid container spacing={2}>
                {!isFieldHidden('startTime') && (
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      type="datetime-local"
                      label="Start Time"
                      value={formData.startTime}
                      onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                      disabled={user?.role === 'team_member' && editingBreak}
                    />
                  </Grid>
                )}
                {!isFieldHidden('endTime') && (
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      type="datetime-local"
                      label="End Time"
                      value={formData.endTime}
                      onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                      disabled={user?.role === 'team_member' && editingBreak}
                    />
                  </Grid>
                )}
              </Grid>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          {editingBreak && (
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={
                savingBreak ||
                !formData.type ||
                (['admin', 'project_manager', 'team_leader', 'cmo'].includes(user?.role) && !formData.userId) ||
                (user?.role === 'team_member' && editingBreak)
              }
            >
              {savingBreak ? <CircularProgress size={24} color="inherit" /> : (user?.role === 'team_member' ? 'Read Only' : 'Update')}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Quick Start Break FAB - Only for team members and not view_only users */}
      {!activeBreak && !['admin', 'project_manager', 'team_leader', 'view_only', 'cmo'].includes(user?.role) && (
        <Tooltip title="Quick Start Break with Timer">
          <Fab
            color="primary"
            sx={{ position: 'fixed', bottom: 16, right: 16 }}
            onClick={() => {
              setFormData({
                ...formData,
                startTime: new Date().toISOString().slice(0, 16),
                type: 'coffee'
              });
              setDialogOpen(true);
            }}
          >
            <StartIcon />
          </Fab>
        </Tooltip>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this break?
          </DialogContentText>
          {breakToDelete && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>Employee:</strong> {breakToDelete.user?.name || 'Unknown'}<br/>
                <strong>Type:</strong> {getBreakTypeInfo(breakToDelete.type).label}<br/>
                <strong>Description:</strong> {breakToDelete.description || 'N/A'}<br/>
                <strong>Start Time:</strong> {new Date(breakToDelete.startTime).toLocaleString()}<br/>
                <strong>End Time:</strong> {breakToDelete.endTime ? new Date(breakToDelete.endTime).toLocaleString() : 'Active'}<br/>
                <strong>Duration:</strong> {breakToDelete.duration ? formatDuration(breakToDelete.duration) : '-'}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deletingBreak}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={deletingBreak}>
            {deletingBreak ? <CircularProgress size={24} color="inherit" /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BreakAndLeisure;
